/* eslint-disable @typescript-eslint/no-var-requires */
import chalk from 'chalk';
import { has, isString, isArray } from 'lodash/fp';
import boxen from 'boxen';
import type { Command } from 'commander';
import { getInquirer } from './get-inquirer';

/**
 * Helper functions for the Strapi CLI
 */
const bytesPerKb = 1024;
const sizes = ['B ', 'KB', 'MB', 'GB', 'TB', 'PB'];

/**
 * Convert bytes to a human readable formatted string, for example "1024" becomes "1KB"
 */
const readableBytes = (bytes: number, decimals = 1, padStart = 0) => {
  if (!bytes) {
    return '0';
  }
  const i = Math.floor(Math.log(bytes) / Math.log(bytesPerKb));
  const result = `${(bytes / bytesPerKb ** i).toFixed(decimals)} ${sizes[i].padStart(2)}`;

  return result.padStart(padStart);
};

// Helper to floor a number to a given number of decimal places
function floorToDecimal(value: number, decimals: number) {
  const factor = 10 ** decimals;
  return Math.floor(value * factor) / factor;
}

/**
 * Convert elapsed time to a human readable formatted string, for example "1024" becomes "1s"
 */
const readableTime = (elapsedTime: number, decimals = 1, padStart = 0): string => {
  let value: number;
  let unit: string;

  if (elapsedTime >= 60000) {
    value = elapsedTime / 60000;
    unit = 'm';
  } else if (elapsedTime >= 1000) {
    value = elapsedTime / 1000;
    unit = 's';
  } else {
    value = elapsedTime;
    unit = 'ms';
  }

  const floored = floorToDecimal(value, decimals);
  const result = `${floored.toFixed(decimals)}${unit}`;
  return result.padStart(padStart);
};

/**
 * Separates transfer progress segments (count, size, elapsed, rate, ETA) instead of nested
 * parentheses — easier to scan in a single terminal line.
 */
const TRANSFER_PROGRESS_FIELD_SEP = ' · ';

/**
 * Stage / prep timing: plain `readableTime` (e.g. `1.2s`) when no ETA; with ETA, append
 * `, ~4.0s remaining` (`~` = approximate). Same base format for every stage; remaining is additive.
 */
const formatElapsedAndMaybeRemainingLabel = (
  elapsedMs: number,
  remainingMs: number | null
): string =>
  remainingMs != null
    ? `${readableTime(elapsedMs)}, ~${readableTime(remainingMs)} remaining`
    : readableTime(elapsedMs);

interface ExitWithOptions {
  logger?: Console;
  prc?: NodeJS.Process;
}

/**
 *
 * Display message(s) to console and then call process.exit with code.
 * If code is zero, console.log and green text is used for messages, otherwise console.error and red text.
 *
 */
const exitWith = (code: number, message?: string | string[], options: ExitWithOptions = {}) => {
  const { logger = console, prc = process } = options;

  const log = (message: string) => {
    if (code === 0) {
      logger.log(chalk.green(message));
    } else {
      logger.error(chalk.red(message));
    }
  };

  if (isString(message)) {
    log(message);
  } else if (isArray(message)) {
    message.forEach((msg) => log(msg));
  }

  prc.exit(code);
};

/**
 * assert that a URL object has a protocol value
 *
 */
const assertUrlHasProtocol = (url: URL, protocol?: string | string[]) => {
  if (!url.protocol) {
    exitWith(1, `${url.toString()} does not have a protocol`);
  }

  // if just checking for the existence of a protocol, return
  if (!protocol) {
    return;
  }

  if (isString(protocol)) {
    if (protocol !== url.protocol) {
      exitWith(1, `${url.toString()} must have the protocol ${protocol}`);
    }
    return;
  }

  // assume an array
  if (!protocol.some((protocol) => url.protocol === protocol)) {
    return exitWith(
      1,
      `${url.toString()} must have one of the following protocols: ${protocol.join(',')}`
    );
  }
};

type ConditionCallback = (opts: Record<string, any>) => Promise<boolean>;
type IsMetCallback = (command: Command) => Promise<void>;
type IsNotMetCallback = (command: Command) => Promise<void>;

/**
 * Passes commander options to conditionCallback(). If it returns true, call isMetCallback otherwise call isNotMetCallback
 */
const ifOptions = (
  conditionCallback: ConditionCallback,
  isMetCallback: IsMetCallback = async () => {},
  isNotMetCallback: IsNotMetCallback = async () => {}
) => {
  return async (command: Command) => {
    const opts = command.opts();
    if (await conditionCallback(opts)) {
      await isMetCallback(command);
    } else {
      await isNotMetCallback(command);
    }
  };
};

const assertCwdContainsStrapiProject = (name: string) => {
  const logErrorAndExit = () => {
    console.log(
      `You need to run ${chalk.yellow(
        `strapi ${name}`
      )} in a Strapi project. Make sure you are in the right directory.`
    );
    process.exit(1);
  };

  try {
    const pkgJSON = require(`${process.cwd()}/package.json`);
    if (
      !has('dependencies.@strapi/strapi', pkgJSON) &&
      !has('devDependencies.@strapi/strapi', pkgJSON)
    ) {
      logErrorAndExit();
    }
  } catch {
    logErrorAndExit();
  }
};

const runAction =
  (name: string, action: (...args: any[]) => Promise<void>) =>
  (...args: unknown[]) => {
    assertCwdContainsStrapiProject(name);

    Promise.resolve()
      .then(() => {
        return action(...args);
      })
      .catch((error) => {
        console.error(error);
        process.exit(1);
      });
  };

/**
 * @description Notify users this is an experimental command and get them to approve first
 * this can be opted out by passing `yes` as a property of the args object.
 *
 * @example
 * ```ts
 * const { notifyExperimentalCommand } = require('../utils/helpers');
 *
 * const myCommand = async ({ force }) => {
 *  await notifyExperimentalCommand('plugin:build', { force });
 * }
 * ```
 */
const notifyExperimentalCommand = async (name: string, { force }: { force?: boolean } = {}) => {
  console.log(
    boxen(
      `The ${chalk.bold(
        chalk.underline(name)
      )} command is considered experimental, use at your own risk.`,
      {
        title: 'Warning',
        padding: 1,
        margin: 1,
        align: 'center',
        borderColor: 'yellow',
        borderStyle: 'bold',
      }
    )
  );

  if (!force) {
    const inquirer = await getInquirer();
    const { confirmed } = await inquirer.prompt({
      type: 'confirm',
      name: 'confirmed',
      message: 'Do you want to continue?',
    });

    if (!confirmed) {
      process.exit(0);
    }
  }
};

export {
  exitWith,
  assertUrlHasProtocol,
  ifOptions,
  readableBytes,
  readableTime,
  formatElapsedAndMaybeRemainingLabel,
  TRANSFER_PROGRESS_FIELD_SEP,
  runAction,
  assertCwdContainsStrapiProject,
  notifyExperimentalCommand,
};
